import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Checkbox,
    Divider,
    FormLabel,
    HStack,
    Icon,
    IconButton,
    Input,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverFooter,
    PopoverTrigger,
    Radio,
    RadioGroup,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Textarea,
    Tooltip,
    Tr,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { TbFlag, TbSend, TbSearch } from 'react-icons/tb';
import { MdCheck } from 'react-icons/md';
import { AskCarlInputRequest, AskCarlMessage, CarlReportData } from '../../types/askCarl';
import SessionContext from '../../contexts/SessionContext';
import { SegmentedControl } from '../../ui';
import PartType from '../PartType';
import { formatPrice } from '../../functions';
import { useRequestQuotes } from '../../hooks/useRequestQuotes';
import { useTogglePartExclusion } from '../../hooks/useTogglePartExclusion';
import { useTogglePartFlag } from '../../hooks/useTogglePartFlag';
import { useUpdateEms } from '../../hooks/useUpdateEms';
import OrderContextCard, { OrderContextSnapshot } from './OrderContextCard';
import PartOptionsCard, { PartOptionsSnapshotData } from './PartOptionsCard';
import ToolStepTimeline from './ToolStepTimeline';
import CarlMessageContent from './CarlMessageContent';
import EmsExportHistoryCard, { EmsExportHistoryData } from './EmsExportHistoryCard';
import OrdersTableCard, { OrdersTableSnapshot } from './OrdersTableCard';
import CarlReportingCard from './CarlReportingCard';

/**
 * Shared rendering for one Ask Carl assistant/user message: the substep
 * timeline (ENG-1711), rich info-graphic containers (quote form, EMS card,
 * order context, part options, export history), and the formatted text body.
 * Used by both the full Ask Carl page and the OrderDetail CarlChatDrawer so
 * a thread renders identically everywhere.
 */

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`;

interface SubmitQuoteModalPartSnapshot {
    _id: string;
    title: string;
    partNumber: string;
    emsPartType: string;
    isDuplicate?: boolean;
    excludeQuoting?: boolean;
    manualFlag?: boolean;
}

interface SubmitQuoteModalSnapshot {
    parts: Array<SubmitQuoteModalPartSnapshot>;
    orderId: string;
    roNumber: string;
    needsPartTypeVerification: boolean;
    allowExpedited: boolean;
    allowDealerReferral: boolean;
    oemCertification: boolean;
    locationName: string;
    makeId?: string;
    alwaysPreferOem?: boolean;
    preferredDeliveryTimePreference: number;
    isCustomerPayOrder: boolean;
    disableAutoOrderCustomerPay: boolean;
    resubmit?: boolean;
}

interface EmsPartSnapshot {
    type: string;
    partNumber: string;
    partName: string;
    listPrice: number;
    quantity: number;
    lineNumber: number;
}

interface EmsMarketplaceSnapshot {
    marketplace: string;
    displayName: string;
}

interface EmsDetailSnapshot {
    emsId: string;
    parts: Array<EmsPartSnapshot>;
    numMarketplaces: number;
    marketplaces: Array<EmsMarketplaceSnapshot>;
    title: string;
    vin: string;
    roNumber: string;
    location: string;
    customer: string;
    insurance: string;
    status: 'submitted' | 'pending';
}


const FlagReasonPopover = ({
    isDisabled,
    onSubmit,
}: {
    isDisabled: boolean;
    onSubmit: (reason: string) => void;
}) => {
    const [flagReason, setFlagReason] = useState('');

    const reset = () => {
        setFlagReason('');
    };

    return (
        <Popover placement="top-start" onClose={reset}>
            {({ onClose }) => (
                <>
                    <PopoverTrigger>
                        <IconButton
                            aria-label="Flag part (note required)"
                            icon={<Icon as={TbFlag as React.ElementType} w={4} h={4} />}
                            size="sm"
                            variant="ghost"
                            color="gray.400"
                            _hover={{ color: 'orange.500', bg: 'gray.100' }}
                            isDisabled={isDisabled}
                        />
                    </PopoverTrigger>
                    <PopoverContent width="320px" bg="white" borderColor="gray.200">
                        <PopoverBody>
                            <Text fontSize="xs" color="gray.600" mb={2}>
                                A note is required before flagging this part.
                            </Text>
                            <Textarea
                                value={flagReason}
                                onChange={(event) => setFlagReason(event.target.value)}
                                placeholder="Enter flag reason..."
                                size="sm"
                                minH="90px"
                                bg="white"
                                borderColor="gray.200"
                                color="gray.900"
                                _placeholder={{ color: 'gray.400' }}
                                _focus={{ borderColor: '#0071E3', boxShadow: '0 0 0 1px #0071E3' }}
                            />
                        </PopoverBody>
                        <PopoverFooter display="flex" justifyContent="flex-end" gap={2} borderColor="gray.100">
                            <Button size="sm" variant="outlineGray" onClick={onClose} color="gray.600" _hover={{ bg: 'gray.100' }}>
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                bg="orange.500"
                                color="white"
                                _hover={{ bg: 'orange.600' }}
                                isDisabled={!flagReason.trim()}
                                onClick={() => {
                                    onSubmit(flagReason.trim());
                                    reset();
                                    onClose();
                                }}
                            >
                                Flag
                            </Button>
                        </PopoverFooter>
                    </PopoverContent>
                </>
            )}
        </Popover>
    );
};

const InlineQuoteSubmissionSnapshot = ({
    quoteModal,
}: {
    quoteModal: SubmitQuoteModalSnapshot;
}) => {
    const sessionContext = useContext(SessionContext);
    const toast = useToast();
    const requestQuotesMutation = useRequestQuotes();
    const toggleExclusionMutation = useTogglePartExclusion();
    const togglePartFlagMutation = useTogglePartFlag();
    const canManageAutoOrder = !!sessionContext?.permissions && sessionContext.permissions.includes('place-order') && sessionContext.permissions.includes('order-flagged-parts');
    const [potentialTotalLoss, setPotentialTotalLoss] = useState(false);
    const [oemEndorsement, setOemEndorsement] = useState(false);
    const [expedited, setExpedited] = useState(false);
    const [quotesOnly, setQuotesOnly] = useState(false);
    // Explicit opt-out for users who can see the Allow Auto-Order checkbox.
    // Everyone else defaults to auto-order on; the API applies org-level gates.
    const [allowAutoOrder, setAllowAutoOrder] = useState(true);
    const [carStatus, setCarStatus] = useState<'pre-order' | 'on-site' | ''>('');
    // Optional free-text message sent to vendors with the quote request
    // (mirrors SubmitQuoteModal's vendorMessage; the old @mention / note-to-self
    // composer was dropped when request-quotes moved to a single vendorMessage).
    const [vendorMessage, setVendorMessage] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const sourceQuoteParts = useMemo(
        () => (Array.isArray(quoteModal.parts) ? quoteModal.parts : []),
        [quoteModal.parts]
    );
    const [quoteParts, setQuoteParts] = useState<Array<SubmitQuoteModalPartSnapshot>>(
        sourceQuoteParts
    );
    const [pendingPartIds, setPendingPartIds] = useState<string[]>([]);
    const autoOrderBlockedForCustomerPay = !!quoteModal.disableAutoOrderCustomerPay && !!quoteModal.isCustomerPayOrder;
    const requiresFlagReason = !sessionContext?.permissions?.includes('order-flagged-parts');
    // Derived, never latched: clearing Quotes Only / Potential Total restores auto-order.
    const autoOrderForcedOff = !!quoteModal.oemCertification
        || autoOrderBlockedForCustomerPay
        || potentialTotalLoss
        || quotesOnly;
    const effectiveAllowAutoOrder = !autoOrderForcedOff && (canManageAutoOrder ? allowAutoOrder : true);

    useEffect(() => {
        setPotentialTotalLoss(false);
        setOemEndorsement(false);
        setExpedited(false);
        setQuotesOnly(false);
        setAllowAutoOrder(true);
        setCarStatus('');
        setVendorMessage('');
        setSubmitted(false);
        setPendingPartIds([]);
        setQuoteParts(sourceQuoteParts);
    }, [quoteModal.orderId, quoteModal.resubmit, sourceQuoteParts]);

    const includedParts = quoteParts.filter((part) => !part.excludeQuoting);
    const excludedParts = quoteParts.filter((part) => part.excludeQuoting);
    const submitLabel = quoteModal.resubmit ? 'Resubmit Quotes' : 'Request Parts';
    const submitDisabled = !carStatus || requestQuotesMutation.isPending;
    const isPartPending = (partId: string) => pendingPartIds.includes(partId);

    const handlePartExclusionToggle = (part: SubmitQuoteModalPartSnapshot) => {
        let previousParts: Array<SubmitQuoteModalPartSnapshot> = [];
        setQuoteParts((currentParts) => {
            previousParts = currentParts;
            return currentParts.map((currentPart) => (
                currentPart._id === part._id
                    ? { ...currentPart, excludeQuoting: !currentPart.excludeQuoting }
                    : currentPart
            ));
        });
        setPendingPartIds((current) => (current.includes(part._id) ? current : [...current, part._id]));
        toggleExclusionMutation.mutate(
            {
                orderPartId: part._id,
                orderId: quoteModal.orderId,
                origin: 'quoting',
            },
            {
                onError: () => {
                    setQuoteParts(previousParts);
                    toast({
                        title: 'Unable to update part exclusion.',
                        status: 'error',
                        duration: 3500,
                        isClosable: true,
                    });
                },
                onSettled: () => {
                    setPendingPartIds((current) => current.filter((pendingId) => pendingId !== part._id));
                },
            }
        );
    };

    const handlePartFlagToggle = (part: SubmitQuoteModalPartSnapshot, flagReason?: string) => {
        let previousParts: Array<SubmitQuoteModalPartSnapshot> = [];
        const nextFlagValue = !part.manualFlag;
        setQuoteParts((currentParts) => {
            previousParts = currentParts;
            return currentParts.map((currentPart) => (
                currentPart._id === part._id
                    ? { ...currentPart, manualFlag: nextFlagValue }
                    : currentPart
            ));
        });
        setPendingPartIds((current) => (current.includes(part._id) ? current : [...current, part._id]));
        togglePartFlagMutation.mutate(
            {
                orderPartId: part._id,
                orderId: quoteModal.orderId,
                ...(nextFlagValue ? { flagReason: flagReason ?? 'Flagged from Ask Carl quote submission.' } : {}),
            },
            {
                onError: () => {
                    setQuoteParts(previousParts);
                    toast({
                        title: 'Unable to update part flag.',
                        status: 'error',
                        duration: 3500,
                        isClosable: true,
                    });
                },
                onSettled: () => {
                    setPendingPartIds((current) => current.filter((pendingId) => pendingId !== part._id));
                },
            }
        );
    };

    const handleSubmitQuote = async () => {
        if (!carStatus || requestQuotesMutation.isPending) return;
        try {
            await requestQuotesMutation.mutateAsync({
                orderId: quoteModal.orderId,
                potentialTotal: potentialTotalLoss,
                preOrder: carStatus === 'pre-order',
                quotesOnly,
                oemEndorsement,
                allowAutoOrder: effectiveAllowAutoOrder,
                expedited: quoteModal.allowExpedited ? expedited : false,
                vendorMessage: vendorMessage.trim(),
                resubmit: quoteModal.resubmit,
            });
            setSubmitted(true);
            toast({
                title: 'Quote submission queued',
                description: `RO ${quoteModal.roNumber || 'N/A'} has been sent for processing.`,
                status: 'success',
                duration: 3500,
                isClosable: true,
            });
        } catch (error) {
            const requestError = error as { response?: { status?: number; data?: { message?: string; }; }; };
            if (requestError?.response?.status === 422) {
                toast({
                    title: requestError.response.data?.message ?? 'Order is still processing. Try again shortly.',
                    status: 'error',
                    duration: 6500,
                    isClosable: true,
                });
                return;
            }
            toast({
                title: 'Quote request was already queued or failed.',
                status: 'info',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="xl"
            bg="white"
            px={4}
            py={3.5}
            boxShadow="none"
        >
            <VStack align="stretch" spacing={3}>
                <HStack justify="space-between" align="flex-start">
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                            Submit Quote
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                            RO #{quoteModal.roNumber || 'N/A'} · {quoteModal.locationName || 'Unknown location'}
                        </Text>
                    </Box>
                    <HStack spacing={2}>
                        <Badge colorScheme="blue" variant="subtle">{includedParts.length} Parts</Badge>
                        {quoteModal.resubmit && <Badge colorScheme="purple" variant="subtle">Resubmit</Badge>}
                        {quoteModal.oemCertification && <Badge colorScheme="purple" variant="subtle">OEM Cert</Badge>}
                    </HStack>
                </HStack>

                {submitted && (
                    <Alert status="success" borderRadius="md" py={2}>
                        <AlertIcon />
                        <Text fontSize="sm" color="green.700">Quotes are queued for this order.</Text>
                    </Alert>
                )}

                {canManageAutoOrder && (
                    <Checkbox
                        isChecked={effectiveAllowAutoOrder}
                        onChange={(event) => setAllowAutoOrder(event.target.checked)}
                        isDisabled={autoOrderForcedOff}
                        colorScheme="blue"
                    >
                        Allow Auto-Order
                    </Checkbox>
                )}
                {autoOrderBlockedForCustomerPay && (
                    <Text fontSize="xs" color="gray.500" mt={-1}>
                        Auto-order is disabled for Customer Pay/Self Pay orders by org settings.
                    </Text>
                )}

                <HStack spacing={4} align="center" flexWrap="wrap">
                    <Checkbox isChecked={potentialTotalLoss} onChange={(event) => setPotentialTotalLoss(event.target.checked)} colorScheme="blue">
                        Potential Total
                    </Checkbox>
                    <Checkbox isChecked={quotesOnly} onChange={(event) => setQuotesOnly(event.target.checked)} isDisabled={quoteModal.oemCertification} colorScheme="blue">
                        Quotes Only
                    </Checkbox>
                    <Checkbox isChecked={oemEndorsement} onChange={(event) => setOemEndorsement(event.target.checked)} colorScheme="blue">
                        OEM Endorsement
                    </Checkbox>
                    {quoteModal.allowExpedited && (
                        <Checkbox isChecked={expedited} onChange={(event) => setExpedited(event.target.checked)} colorScheme="blue">
                            Expedited
                        </Checkbox>
                    )}
                </HStack>

                <Box>
                    <FormLabel fontSize="sm" mb={1.5} color="gray.700">
                        Car Status
                    </FormLabel>
                    <RadioGroup value={carStatus} onChange={(value) => setCarStatus(value as 'pre-order' | 'on-site' | '')}>
                        <HStack spacing={6}>
                            <Radio value="pre-order" colorScheme="blue">Pre-order</Radio>
                            <Radio value="on-site" colorScheme="blue">On site</Radio>
                        </HStack>
                    </RadioGroup>
                </Box>

                <Box>
                    <FormLabel fontSize="sm" mb={1.5} color="gray.700">
                        Message to vendors (optional)
                    </FormLabel>
                    <Textarea
                        value={vendorMessage}
                        onChange={(event) => setVendorMessage(event.target.value)}
                        placeholder="Enter an optional message to send to vendors..."
                        minH="64px"
                        resize="vertical"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{ borderColor: '#0071E3', boxShadow: '0 0 0 1px #0071E3' }}
                        color="gray.900"
                        _placeholder={{ color: 'gray.400' }}
                    />
                </Box>

                <Divider borderColor="gray.200" />

                <VStack align="stretch" spacing={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        Included Parts
                    </Text>
                    {includedParts.length ? includedParts.map((part) => (
                        <HStack key={part._id} justify="space-between" border="1px solid" borderColor="gray.200" borderRadius="md" px={3} py={2} align="flex-start" bg="gray.50">
                            <HStack spacing={2} align="center" minW="90px">
                                <Button
                                    size="xs"
                                    variant="outline"
                                    borderColor="gray.300"
                                    color="gray.600"
                                    _hover={{ bg: 'gray.100' }}
                                    onClick={() => handlePartExclusionToggle(part)}
                                    isDisabled={isPartPending(part._id)}
                                >
                                    Exclude
                                </Button>
                                {requiresFlagReason && !part.manualFlag ? (
                                    <FlagReasonPopover
                                        isDisabled={isPartPending(part._id)}
                                        onSubmit={(reason) => handlePartFlagToggle(part, reason)}
                                    />
                                ) : (
                                    <IconButton
                                        aria-label={part.manualFlag ? 'Unflag part' : 'Flag part'}
                                        icon={<Icon as={TbFlag as React.ElementType} w={4} h={4} fill={part.manualFlag ? 'currentColor' : 'none'} />}
                                        size="sm"
                                        variant="ghost"
                                        color={part.manualFlag ? 'orange.500' : 'gray.400'}
                                        _hover={{ bg: 'gray.100' }}
                                        onClick={() => handlePartFlagToggle(part)}
                                        isDisabled={isPartPending(part._id)}
                                    />
                                )}
                            </HStack>
                            <Box flex={1}>
                                <Text fontSize="sm" color="gray.800">{part.title}</Text>
                                <Text fontSize="xs" color="gray.500" fontFamily="mono">OE #: {part.partNumber}</Text>
                            </Box>
                            <HStack spacing={2}>
                                {part.isDuplicate && (
                                    <Tooltip label="Duplicate part" placement="top" hasArrow>
                                        <Badge px={2} py={0.5} borderRadius="full" colorScheme="orange" variant="subtle" fontSize="xs" fontWeight="medium">
                                            DUP
                                        </Badge>
                                    </Tooltip>
                                )}
                                {part.manualFlag && <Badge colorScheme="orange" variant="subtle">Flagged</Badge>}
                                <PartType partType={part.emsPartType} />
                            </HStack>
                        </HStack>
                    )) : (
                        <Text fontSize="sm" color="gray.500">No quotable parts were returned.</Text>
                    )}
                </VStack>

                {excludedParts.length > 0 && (
                    <VStack align="stretch" spacing={2}>
                        <Text fontSize="sm" fontWeight="semibold" color="red.600">
                            Excluded Parts
                        </Text>
                        {excludedParts.map((part) => (
                            <HStack key={part._id} justify="space-between" border="1px solid" borderColor="red.200" bg="red.50" borderRadius="md" px={3} py={2} align="flex-start">
                                <HStack spacing={2} align="center" minW="90px">
                                    <Button
                                        size="xs"
                                        variant="outline"
                                        borderColor="gray.300"
                                        color="gray.600"
                                        bg="white"
                                        _hover={{ bg: 'gray.100' }}
                                        onClick={() => handlePartExclusionToggle(part)}
                                        isDisabled={isPartPending(part._id)}
                                    >
                                        Include
                                    </Button>
                                    {requiresFlagReason && !part.manualFlag ? (
                                        <FlagReasonPopover
                                            isDisabled={isPartPending(part._id)}
                                            onSubmit={(reason) => handlePartFlagToggle(part, reason)}
                                        />
                                    ) : (
                                        <IconButton
                                            aria-label={part.manualFlag ? 'Unflag part' : 'Flag part'}
                                            icon={<Icon as={TbFlag as React.ElementType} w={4} h={4} fill={part.manualFlag ? 'currentColor' : 'none'} />}
                                            size="sm"
                                            variant="ghost"
                                            color={part.manualFlag ? 'orange.500' : 'gray.400'}
                                            _hover={{ bg: 'gray.100' }}
                                            onClick={() => handlePartFlagToggle(part)}
                                            isDisabled={isPartPending(part._id)}
                                        />
                                    )}
                                </HStack>
                                <Box flex={1}>
                                    <Text fontSize="sm" color="gray.800">{part.title}</Text>
                                    <Text fontSize="xs" color="gray.500" fontFamily="mono">OE #: {part.partNumber}</Text>
                                </Box>
                                <HStack spacing={2}>
                                    {part.isDuplicate && (
                                        <Tooltip label="Duplicate part — excluded because this line already exists on the estimate" placement="top" hasArrow>
                                            <Badge px={2} py={0.5} borderRadius="full" colorScheme="orange" variant="subtle" fontSize="xs" fontWeight="medium">
                                                DUP
                                            </Badge>
                                        </Tooltip>
                                    )}
                                    {part.manualFlag && <Badge colorScheme="orange" variant="subtle">Flagged</Badge>}
                                    <PartType partType={part.emsPartType} />
                                </HStack>
                            </HStack>
                        ))}
                    </VStack>
                )}

                <Button
                    alignSelf="flex-end"
                    bg="#0071E3"
                    color="white"
                    _hover={{ bg: '#005BC4' }}
                    _active={{ bg: '#28337d' }}
                    transition="all 0.2s"
                    onClick={handleSubmitQuote}
                    isLoading={requestQuotesMutation.isPending}
                    loadingText="Submitting..."
                    isDisabled={submitDisabled}
                >
                    {submitLabel}
                </Button>
            </VStack>
        </Box>
    );
};

const InlineEmsResubmitSnapshot = ({
    emsDetail,
}: {
    emsDetail: EmsDetailSnapshot;
}) => {
    const toast = useToast();
    const updateEmsMutation = useUpdateEms();
    const [showPartsOnly, setShowPartsOnly] = useState(false);

    const allParts = Array.isArray(emsDetail.parts) ? emsDetail.parts : [];
    const filteredParts = showPartsOnly
        ? allParts.filter((part) => part.partNumber && part.listPrice > 0)
        : allParts;

    const handleSend = async () => {
        if (!emsDetail.emsId) return;
        try {
            await updateEmsMutation.mutateAsync({ emsId: emsDetail.emsId });
            toast({
                title: 'EMS Sent',
                description: 'Please wait until your order populates in the Dashboard.',
                status: 'success',
                duration: 6000,
                isClosable: true,
            });
        } catch (error) {
            const requestError = error as { response?: { data?: { message?: string; }; }; };
            toast({
                title: 'Push Failed',
                description: requestError?.response?.data?.message ?? 'Please try again.',
                status: 'error',
                duration: 8000,
                isClosable: true,
            });
        }
    };

    return (
        <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="xl"
            bg="white"
            px={4}
            py={3.5}
            boxShadow="none"
        >
            <VStack align="stretch" spacing={3}>
                <HStack justify="space-between" align="flex-start">
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                            EMS Resubmit
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                            {emsDetail.title} · RO #{emsDetail.roNumber || '—'}
                        </Text>
                    </Box>
                    <Badge
                        colorScheme={emsDetail.status === 'submitted' ? 'green' : 'orange'}
                        variant="subtle"
                        textTransform="capitalize"
                    >
                        {emsDetail.status}
                    </Badge>
                </HStack>

                <HStack spacing={4} align="center" justify="space-between" flexWrap="wrap">
                    <SegmentedControl
                        size="sm"
                        value={showPartsOnly ? 'parts' : 'all'}
                        onChange={(next) => setShowPartsOnly(next === 'parts')}
                        items={[
                            { value: 'all', label: 'All' },
                            { value: 'parts', label: 'Parts' },
                        ]}
                    />

                    <HStack spacing={2}>
                        {emsDetail.status === 'submitted' && Array.isArray(emsDetail.marketplaces) && emsDetail.marketplaces.map((marketplace) => (
                            <Box
                                key={marketplace.marketplace}
                                display="flex"
                                alignItems="center"
                                gap={1}
                                px={2.5}
                                py={1}
                                border="1px solid"
                                borderColor="green.300"
                                borderRadius="full"
                                bg="green.50"
                                color="green.700"
                                fontSize="xs"
                                fontWeight="medium"
                            >
                                <Icon as={MdCheck as React.ElementType} boxSize={3} />
                                {marketplace.displayName}
                            </Box>
                        ))}
                        <Button
                            size="sm"
                            bg="#0071E3"
                            color="white"
                            _hover={{ bg: '#005BC4' }}
                            onClick={handleSend}
                            isLoading={updateEmsMutation.isPending}
                            isDisabled={!emsDetail.emsId || emsDetail.numMarketplaces === 0}
                        >
                            {`${emsDetail.status === 'submitted' ? 'Re-' : ''}Send to Marketplaces`}
                        </Button>
                    </HStack>
                </HStack>

                <TableContainer border="1px solid" borderColor="gray.200" borderRadius="lg" bg="gray.50">
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200">Line #</Th>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200">Qty</Th>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200">Part Type</Th>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200">Description</Th>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200">OE Part #</Th>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200" isNumeric>Price</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {filteredParts.map((part, index) => (
                                <Tr key={`${part.partNumber}-${index}`}>
                                    <Td fontSize="xs" color="gray.700" borderColor="gray.200">{part.lineNumber}</Td>
                                    <Td fontSize="xs" color="gray.700" borderColor="gray.200">{part.quantity}</Td>
                                    <Td fontSize="xs" borderColor="gray.200"><PartType partType={part.type} /></Td>
                                    <Td fontSize="xs" color="gray.700" borderColor="gray.200">{part.partName}</Td>
                                    <Td fontSize="xs" color="gray.700" borderColor="gray.200" fontFamily="mono">{part.partNumber}</Td>
                                    <Td fontSize="xs" color="gray.700" borderColor="gray.200" isNumeric>{formatPrice(part.listPrice)}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            </VStack>
        </Box>
    );
};

const renderPartSnapshots = (payload?: Record<string, unknown>) => {
    const partOptions = payload?.partOptions as PartOptionsSnapshotData | undefined;
    if (!partOptions) return null;

    return (
        <VStack mt={3} spacing={3} align="stretch">
            <PartOptionsCard partOptions={partOptions} />
        </VStack>
    );
};

const renderQuoteSubmissionSnapshot = (
    payload: Record<string, unknown> | undefined
) => {
    const quoteModal = payload?.submitQuoteModal as SubmitQuoteModalSnapshot | undefined;
    const orderDetail = payload?.orderDetail as OrderContextSnapshot | undefined;
    if (!quoteModal?.orderId) return null;

    return (
        <VStack mt={3} spacing={3} align="stretch">
            <InlineQuoteSubmissionSnapshot quoteModal={quoteModal} />
            {orderDetail && <OrderContextCard order={orderDetail} />}
        </VStack>
    );
};

const renderEmsResubmitSnapshot = (
    payload: Record<string, unknown> | undefined
) => {
    const emsDetail = payload?.emsDetail as EmsDetailSnapshot | undefined;
    const orderDetail = payload?.orderDetail as OrderContextSnapshot | undefined;
    if (!emsDetail?.emsId) return null;

    return (
        <VStack mt={3} spacing={3} align="stretch">
            <InlineEmsResubmitSnapshot emsDetail={emsDetail} />
            {orderDetail && <OrderContextCard order={orderDetail} />}
        </VStack>
    );
};

/** Standalone order info-graphic (from resolve_order_context) when no quote/EMS container owns it. */
const renderOrderContext = (payload: Record<string, unknown> | undefined) => {
    const orderDetail = payload?.orderDetail as OrderContextSnapshot | undefined;
    const hasOwner = Boolean(payload?.submitQuoteModal) || Boolean(payload?.emsDetail);
    if (!orderDetail || hasOwner) return null;

    return (
        <VStack mt={3} spacing={3} align="stretch">
            <OrderContextCard order={orderDetail} />
        </VStack>
    );
};

const renderEmsExportHistory = (payload: Record<string, unknown> | undefined) => {
    const history = payload?.emsExportHistory as EmsExportHistoryData | undefined;
    if (!history?.exports?.length) return null;

    return (
        <VStack mt={3} spacing={3} align="stretch">
            <EmsExportHistoryCard history={history} />
        </VStack>
    );
};

/** Interactive matching-orders table (from query_orders), rows linking to each order. */
const renderOrdersTable = (payload: Record<string, unknown> | undefined) => {
    const table = payload?.ordersTable as OrdersTableSnapshot | undefined;
    if (!table?.orders?.length) return null;

    return (
        <VStack mt={3} spacing={3} align="stretch">
            <OrdersTableCard table={table} />
        </VStack>
    );
};

/** Reporting chart/table card (ENG-2169) from the permission-gated reporting tools. */
const renderReportingCard = (payload: Record<string, unknown> | undefined) => {
    const report = payload?.reportingCard as CarlReportData | undefined;
    if (!report?.kind) return null;

    return (
        <VStack mt={3} spacing={3} align="stretch">
            <CarlReportingCard report={report} />
        </VStack>
    );
};


/**
 * Question/approval card for a pending eve input request. Approvals render
 * Approve/Deny; questions render selectable option rows plus an inline
 * freeform input to narrow down with an RO/VIN/customer/claim not listed.
 */
export const PendingInputCard = ({
    request,
    isLoading,
    onRespond,
}: {
    request: AskCarlInputRequest;
    isLoading: boolean;
    onRespond: (requestId: string, response: { optionId?: string; text?: string; }) => void;
}) => {
    const [answerText, setAnswerText] = useState('');
    const isApproval = request.display === 'confirmation'
        || (!request.options?.length && request.toolName !== 'ask_question');
    // Hide the generic "Approve tool call: <tool>" prompt — the real preview
    // is in Carl's message above; the card just needs the approve/deny action.
    const isGenericApprovalPrompt = isApproval && /^approve tool call:/i.test(request.prompt.trim());

    const submitFreeform = () => {
        if (!answerText.trim() || isLoading) return;
        onRespond(request.requestId, { text: answerText.trim() });
        setAnswerText('');
    };

    return (
        <Box
            alignSelf="flex-start"
            bg="white"
            borderRadius="2xl"
            px={4}
            py={3}
            maxW="95%"
            w={isApproval ? undefined : { base: '95%', md: '560px' }}
            border="1px solid"
            borderColor="#0071E3"
            boxShadow="none"
            animation={`${fadeIn} 0.3s ease-out`}
        >
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                {isApproval ? 'Carl needs your approval' : 'Carl has a question'}
            </Text>
            {!isGenericApprovalPrompt && (
                <Text fontSize="sm" color="gray.600" mt={1} whiteSpace="pre-wrap">
                    {request.prompt}
                </Text>
            )}

            {isApproval ? (
                <HStack spacing={2} mt={3}>
                    {[
                        { id: 'approve', label: 'Approve', style: 'primary' as const },
                        { id: 'deny', label: 'Deny', style: 'danger' as const },
                    ].map((option) => (
                        <Button
                            key={option.id}
                            size="sm"
                            bg={option.style === 'primary' ? '#0071E3' : 'transparent'}
                            color={option.style === 'danger' ? 'red.500' : 'white'}
                            border={option.style === 'danger' ? '1px solid' : 'none'}
                            borderColor="red.300"
                            _hover={option.style === 'primary' ? { bg: '#005BC4' } : { bg: 'red.50' }}
                            onClick={() => onRespond(request.requestId, { optionId: option.id })}
                            isDisabled={isLoading}
                        >
                            {option.label}
                        </Button>
                    ))}
                </HStack>
            ) : (
                <VStack spacing={1.5} mt={3} align="stretch">
                    {(request.options ?? []).map((option) => (
                        <Box
                            key={option.id}
                            as="button"
                            textAlign="left"
                            px={3}
                            py={2}
                            borderRadius="lg"
                            border="1px solid"
                            borderColor="gray.200"
                            bg="white"
                            _hover={{ borderColor: '#0071E3', bg: 'blue.50' }}
                            transition="all 0.15s"
                            onClick={() => onRespond(request.requestId, { optionId: option.id })}
                            disabled={isLoading}
                        >
                            <Text fontSize="sm" color="gray.800" fontWeight="medium">
                                {option.label}
                            </Text>
                            {option.description && (
                                <Text fontSize="xs" color="gray.500" mt={0.5}>
                                    {option.description}
                                </Text>
                            )}
                        </Box>
                    ))}
                    {request.allowFreeform !== false && (
                        <HStack
                            mt={1.5}
                            spacing={1}
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                            px={2}
                            py={1}
                            _focusWithin={{ borderColor: '#0071E3' }}
                        >
                            <Icon as={TbSearch as React.ElementType} boxSize={4} color="gray.400" flexShrink={0} />
                            <Input
                                value={answerText}
                                onChange={(event) => setAnswerText(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        submitFreeform();
                                    }
                                }}
                                placeholder="Or enter an RO #, VIN, customer name, or claim #..."
                                variant="unstyled"
                                size="sm"
                                color="gray.900"
                                _placeholder={{ color: 'gray.400' }}
                                px={1}
                            />
                            <IconButton
                                aria-label="Submit answer"
                                icon={<Icon as={TbSend as React.ElementType} boxSize={4} />}
                                size="xs"
                                variant="ghost"
                                color={answerText.trim() ? '#0071E3' : 'gray.300'}
                                _hover={{ bg: 'gray.100' }}
                                onClick={submitFreeform}
                                isDisabled={!answerText.trim() || isLoading}
                            />
                        </HStack>
                    )}
                </VStack>
            )}
        </Box>
    );
};


const skeletonShimmer = keyframes`
    0% { background-position: -300px 0; }
    100% { background-position: 300px 0; }
`;

/** Pulsing placeholder shown while the response text / next card is still generating. */
const StreamingSkeleton = ({ hasContentAbove }: { hasContentAbove: boolean; }) => {
    const line = (width: string, height = '10px') => (
        <Box
            h={height}
            w={width}
            borderRadius="md"
            sx={{
                background: 'linear-gradient(90deg, rgba(0,0,0,0.04) 25%, rgba(0, 113, 227,0.15) 50%, rgba(0,0,0,0.04) 75%)',
                backgroundSize: '300px 100%',
                animation: `${skeletonShimmer} 1.3s linear infinite`,
            }}
        />
    );
    return (
        <VStack align="stretch" spacing={2} mt={hasContentAbove ? 3 : 1} aria-label="Carl is preparing the response">
            {line('42%', '12px')}
            {line('96%')}
            {line('88%')}
            {line('64%')}
        </VStack>
    );
};

/** Full assistant/user message body — timeline, info-graphics, then text. */
export const CarlMessageBody = ({ message, isStreaming = false }: { message: AskCarlMessage; isStreaming?: boolean; }) => {
    const hasAssistantUi = message.role === 'assistant' && !!message.ui;
    const hasTimeline = message.role === 'assistant' && !!message.toolEvents?.length;
    const submitQuoteModal = message.ui?.submitQuoteModal as SubmitQuoteModalSnapshot | undefined;
    const emsDetail = message.ui?.emsDetail as EmsDetailSnapshot | undefined;
    // While the turn streams and no final text/interactive card has landed yet,
    // show a shimmering placeholder instead of an abruptly empty bubble.
    const showSkeleton = isStreaming
        && message.role === 'assistant'
        && !message.content
        && !submitQuoteModal
        && !emsDetail;

    return (
        <>
            {message.role === 'assistant' && message.toolEvents && message.toolEvents.length > 0 && (
                <ToolStepTimeline steps={message.toolEvents} />
            )}
            {showSkeleton && <StreamingSkeleton hasContentAbove={hasTimeline || hasAssistantUi} />}
            {message.role === 'assistant' && renderQuoteSubmissionSnapshot(message.ui)}
            {message.role === 'assistant' && renderEmsResubmitSnapshot(message.ui)}
            {message.role === 'assistant' && renderOrderContext(message.ui)}
            {message.role === 'assistant' && renderEmsExportHistory(message.ui)}
            {message.role === 'assistant' && renderOrdersTable(message.ui)}
            {message.role === 'assistant' && renderReportingCard(message.ui)}
            {message.role === 'assistant' && renderPartSnapshots(message.ui)}
            {(() => {
                if (!submitQuoteModal && !emsDetail) {
                    if (!message.content) return null;
                    if (message.role === 'assistant') {
                        return (
                            <Box mt={hasAssistantUi || hasTimeline ? 2 : 0}>
                                <CarlMessageContent content={message.content} />
                            </Box>
                        );
                    }
                    return (
                        <Text whiteSpace="pre-wrap" lineHeight="tall">
                            {message.content}
                        </Text>
                    );
                }
                if (submitQuoteModal) {
                    const parts = Array.isArray(submitQuoteModal.parts) ? submitQuoteModal.parts : [];
                    const newPartCount = parts.filter((part) => !part.excludeQuoting).length;
                    const label = newPartCount === 1 ? 'part' : 'parts';
                    return (
                        <Text whiteSpace="pre-wrap" mt={2} lineHeight="tall">
                            {`You have ${newPartCount} new ${label}.`}
                        </Text>
                    );
                }
                return (
                    <Text whiteSpace="pre-wrap" mt={2} lineHeight="tall">
                        Review EMS details and send to marketplaces when ready.
                    </Text>
                );
            })()}
        </>
    );
};

export default CarlMessageBody;
